import { v4 as uuidv4 } from 'uuid'
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Build a point-annotation for spheres/cylinders.
 * - centeredPosition/centeredNormal = exact top-of-primitive (use for drawing the label)
 * - geometry.position = centeredPosition
 * - geometry.normal   = centeredNormal, but slightly "de-verticalized" if too close to world-up/down
 *
 * This avoids tilt-lock when your zoom function uses geometry.{position, normal}.
 */
export function buildTopLabelFromPrimitive(
  primitive: any,
  opts: {
    padding?: number
    severity?: string
    label?: string
    verticalDotThreshold?: number
    nudgeAngleDeg?: number
    worldUp?: { x: number; y: number; z: number }
  } = {}
) {
  const {
    padding = 0.2,
    severity = '0',
    label = primitive.primitiveLabel ?? 'label',
    verticalDotThreshold = 0.98, // ~11.5° cone around up/down
    nudgeAngleDeg = 10, // tilt away from vertical when inside the cone
    worldUp = { x: 0, y: 0, z: 1 },
  } = opts

  // ---------- math helpers ----------
  const V = (x = 0, y = 0, z = 0) => ({ x, y, z })
  const add = (a: any, b: any) => V(a.x + b.x, a.y + b.y, a.z + b.z)
  const mul = (v: any, s: number) => V(v.x * s, v.y * s, v.z * s)
  const dot = (a: any, b: any) => a.x * b.x + a.y * b.y + a.z * b.z
  const len = (v: any) => Math.hypot(v.x, v.y, v.z)
  const norm = (v: any) => {
    const L = len(v) || 1
    return V(v.x / L, v.y / L, v.z / L)
  }
  const cross = (a: any, b: any) =>
    V(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x)
  const rotateByQuat = (v: any, q: any) => {
    const qv = V(q.x, q.y, q.z),
      t = mul(cross(qv, v), 2)
    return add(v, add(mul(t, q.w), cross(qv, t)))
  }
  const toVec = (arr: any) => V(arr?.[0] ?? 0, arr?.[1] ?? 0, arr?.[2] ?? 0)
  const toQuat = (arr: any) => ({
    x: arr?.[0] ?? 0,
    y: arr?.[1] ?? 0,
    z: arr?.[2] ?? 0,
    w: arr?.[3] ?? 1,
  })

  // ---- extract primitive transform ----
  const t = primitive.transform ?? {}
  const center = toVec(t.location ?? [0, 0, 0]) // center of sphere
  const scale = toVec(t.scale ?? [1, 1, 1])
  let quat = toQuat(t.rotation ?? [0, 0, 0, 1]) // (x,y,z,w)
  // normalize quat defensively
  {
    const qL = Math.hypot(quat.x, quat.y, quat.z, quat.w) || 1
    quat = { x: quat.x / qL, y: quat.y / qL, z: quat.z / qL, w: quat.w / qL }
  }

  // local axes rotated to world (useful for choosing a stable sideways reference)
  const zAxisW = norm(rotateByQuat(V(0, 0, 1), quat)) // “top” axis
  const xAxisW = norm(rotateByQuat(V(1, 0, 0), quat)) // sideways fallback

  // --------- compute label anchor (top-of-primitive) ----------
  const kind = (primitive.geometry ?? primitive.type ?? '').toLowerCase()
  let surfaceOffset = padding

  if (kind === 'sphere' || primitive.sphere_data) {
    const r =
      (primitive.sphere_data?.radius ?? 0) * Math.max(scale.x, scale.y, scale.z)
    surfaceOffset += r
  } else if (kind === 'cylinder' || primitive.cylinder_data) {
    const h = (primitive.cylinder_data?.height ?? 0) * (scale.z ?? 1)
    surfaceOffset += 0.5 * h
  } else {
    surfaceOffset += 0.5 * Math.max(scale.x, scale.y, scale.z) // generic fallback
  }

  const centeredPosition = add(center, mul(zAxisW, surfaceOffset))
  const centeredNormal = norm(zAxisW)

  // ---------- de-verticalize the view normal (to avoid tilt lock) ----------
  let viewNormal = centeredNormal
  const worldUpN = norm(worldUp)
  const upDot = Math.abs(dot(viewNormal, worldUpN))
  if (upDot >= verticalDotThreshold) {
    // we’re too close to world-up/down; tilt by nudgeAngleDeg toward a stable sideways ref.
    // pick a sideways reference that’s not colinear with the normal
    let side = cross(viewNormal, worldUpN)
    if (len(side) < 1e-4) {
      // viewNormal is (anti)parallel to worldUp; use rotated local X instead
      side = cross(viewNormal, xAxisW)
    }
    side = norm(side)
    // rotate viewNormal around 'side' by -nudgeAngle to lean it off the pole
    const ang = (nudgeAngleDeg * Math.PI) / 180
    // build a small tilt toward (viewNormal × side) which points around the ring
    const around = cross(side, viewNormal) // points toward the horizontal ring direction
    viewNormal = norm(
      add(mul(viewNormal, Math.cos(ang)), mul(around, Math.sin(ang)))
    )
  }

  const annotationId = uuidv4().replace(/-/g, '').toUpperCase()

  return {
    geometry: {
      kind: 'point',
      position: centeredPosition, // where you render the label
      normal: viewNormal, // what your zoom uses; nudged off vertical if needed
    },
    userName: primitive.primitiveUserName ?? '',
    userEmail: primitive.primitiveUserEmail ?? '',
    annotationId,
    centeredPosition,
    centeredNormal, // keep the exact surface normal for reference
    annotationLabel: label,
    annotationNotes: primitive.primitiveNotes ?? '',
    annotationVisible: true,
    annotationSeverity: severity,
    annotationImageNames: [],
    isSegmentationSurfaceArea: false,
  }
}
